import { useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Menu } from "lucide-react";
import { useLanguage, LANGUAGES } from "@/i18n";
import { waLink } from "@/whatsapp";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const reviewLabel = { fr: "Avis", en: "Reviews", bs: "Recenzije", sq: "Vlerësime", ar: "آراء" }[lang] || "Avis";

  const links = [
    { to: "/#services", label: t.nav.services },
    { to: "/#forfaits", label: t.nav.packages },
    { to: "/#zones", label: t.nav.zones },
    { to: "/#process", label: t.nav.process },
    { to: "/#avis", label: reviewLabel },
    { to: "/#faq", label: t.nav.faq },
    { to: "/#contact", label: t.nav.contact },
  ];

  const LangSwitcher = ({ mobile = false }) => (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-switcher"
        className={`flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-ivory/80 hover:text-gold transition-colors duration-300 outline-none ${mobile ? "px-1 py-2" : ""}`}
      >
        <Globe className="w-4 h-4 text-gold" />
        {LANGUAGES.find((l) => l.code === lang)?.short}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-surface border-hairline" align="end">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-option-${l.code}`}
            onClick={() => setLang(l.code)}
            className={`font-mono text-xs tracking-widest uppercase cursor-pointer ${
              lang === l.code ? "text-gold" : "text-ivory/70"
            } hover:text-gold focus:text-gold focus:bg-midnight`}
          >
            {l.short} — {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-obsidian/85 border-b border-hairline/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" aria-label="ATLAS PROPERTY INTELLIGENCE">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-[13px] font-medium tracking-wide text-ivory/70 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <LangSwitcher />
          <a
            href={waLink(t.wa.msgVisit)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-cta"
            className="bg-gold text-obsidian font-semibold text-[13px] tracking-wide px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors duration-300"
          >
            {t.nav.book}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-4">
          <LangSwitcher mobile />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger data-testid="mobile-menu-button" aria-label="Menu" className="text-ivory">
              <Menu className="w-6 h-6" />
            </SheetTrigger>
            <SheetContent side="right" className="bg-midnight border-hairline w-[300px]">
              <nav className="flex flex-col gap-6 mt-10">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="text-lg font-medium text-ivory/80 hover:text-gold transition-colors"
                  >
                    {l.label}
                  </Link>
                ))}
                <a
                  href={waLink(t.wa.msgVisit)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-book-cta"
                  onClick={() => setOpen(false)}
                  className="mt-4 bg-gold text-obsidian text-center font-semibold text-sm tracking-wide px-5 py-3 rounded-full hover:bg-gold-light transition-colors"
                >
                  {t.nav.book}
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
